<script setup lang="ts">
import { ref, watch } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useVuelidate } from "@vuelidate/core";
import { required, email as emailValidator } from "@vuelidate/validators";
import { useRoute, useRouter } from "vue-router";
import { useCurrentUser } from "vuefire";

const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();
const user = useCurrentUser();

// Runs when the user state changes
watch(user, (currentUser) => {
  if (currentUser) {
    // If a user is signed in, redirect them to the requested page
    const redirectPath = (route.query.redirect as string) || '/';
    router.push(redirectPath);
  }
  // Run on component load
}, { immediate: true });

const email = ref<string>("");
const errorMessage = ref<string>("");

const RESEND_EMAIL_LOCK_SECONDS = 10;
const isResendLock = ref<boolean>(false);
const resendEmailLockSecondsLeft = ref<number>(0);
const resendEmailCountdown = ref<ReturnType<typeof setTimeout>>();

const startResendEmailCountdown = () => {
  resendEmailLockSecondsLeft.value = RESEND_EMAIL_LOCK_SECONDS;
  isResendLock.value = true;

  resendEmailCountdown.value = setInterval(() => {
    resendEmailLockSecondsLeft.value--;

    if (resendEmailLockSecondsLeft.value === 0) {
      clearInterval(resendEmailCountdown.value);
      isResendLock.value = false;
    }
  }, 1000);
};

const stopResendEmailCountdown = () => {
  resendEmailLockSecondsLeft.value = 0;
  isResendLock.value = false;
  clearInterval(resendEmailCountdown.value);
};

const handleResendEmailButton = async (event: Event) => {
  event.preventDefault();
  errorMessage.value = '';

  await authStore
    .resendEmail()
    .then(startResendEmailCountdown)
    .catch((error: Error) => {
      errorMessage.value = error.message ?? error;
    });
};

const handleChangeEmailButton = (event: Event) => {
  event.preventDefault();
  authStore.clearEmailForSignin();
  errorMessage.value = '';
  stopResendEmailCountdown();
};

const handleSubmitButton = async (event: Event) => {
  event.preventDefault();
  errorMessage.value = '';
  
  await authStore
    .sendEmail(email.value)
    .then(startResendEmailCountdown)
    .catch((error: Error) => {
      errorMessage.value = error.message ?? error;
    });
};

const rules = {
  email: { required, email: emailValidator },
};
const v$ = useVuelidate(rules, { email });
</script>

<template>
  <div class="card mx-auto">
    <div class="card-body">
      <h1 v-if="!authStore.emailSent" class="card-title signin-field-text">
        Sign in with your email
      </h1>
      <h1 v-else class="card-title">
        Check your email
      </h1>

      <div v-if="!authStore.emailSent">
        <div class="form-group mb-4">
          <input
            id="email"
            v-model="v$.email.$model"
            type="email"
            placeholder="Enter your email"
            class="form-control"
          >
        </div>
        <div class="d-grid">
          <button
            id="login"
            :disabled="v$.email.$invalid"
            @keydown.enter="handleSubmitButton"
            @click="handleSubmitButton"
          >
            Submit
          </button>
        </div>
        <div class="mt-3">
          <small>
            We&apos;ll email you a special link for a password-free sign-in
          </small>
        </div>
         <div id="support-message-form" class="support-message mt-3">
          <p>
            We now use email verification for secure access. Enter your email, and we&apos;ll send you a link. You&apos;ll stay logged in for a set period.
          </p>
          <p>
            <strong>Need help or want to register a new email?</strong> Email <a href="mailto:clientsupport@nla.co.uk">clientsupport@nla.co.uk</a>
          </p>
        </div>
      </div>
      <div v-else id="email-sent" class="d-grid gap-3">
        <div>
          An email has been sent. If you are a registered user you can use the link included to log in.
        </div>

        <div>
          The link will expire after 6 hours, after which you will need to request a new one.
        </div>

        <br>

        <div>
          <small>
            Having problems?
          </small>
        </div>

        <div class="d-grid gap-2">
          <button
            id="resend"
            class="primary"
            :disabled="isResendLock"
            @click="handleResendEmailButton"
          >
            <span class="button-content">
              <span>Resend email{{ isResendLock ? ` (${resendEmailLockSecondsLeft} s)` : "" }}</span>
            </span>
          </button>

          <hr class="separator">

          <button
            id="change"
            class="secondary"
            @click="handleChangeEmailButton"
          >
            Change email address
          </button>
        </div>
        <div id="support-message-sent" class="support-message">
          <p>
            <strong>Need help or want to register a new email?</strong> Email <a href="mailto:clientsupport@nla.co.uk">clientsupport@nla.co.uk</a>
          </p>
        </div>
      </div>

      <div
        v-if="errorMessage"
        id="error-message"
        class="error-message mt-2"
      >
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>
